"use client";

import * as anchor from "@coral-xyz/anchor";
import Idl from "../../idl/vesting.json";
import { useEffect, useState } from "react";
import { PublicKey } from "@solana/web3.js";
import { TOKEN_MINT, VESTING_PROGRAM_ID } from "utils/envs";
import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import { VestingAccount } from "types/account";
import { formatAmount, shortenAddress } from "utils";
import {
  createTransferInstruction,
  getAssociatedTokenAddress,
  getAccount,
} from "@solana/spl-token";
import { FiClipboard, FiCheck } from "react-icons/fi";
import { HIDDEN_VESTING_LIST } from "utils/constants";

const PROGRAM_ID = new PublicKey(VESTING_PROGRAM_ID);

const VestingCreatedList = () => {
  const {
    publicKey,
    signTransaction,
    signAllTransactions,
    sendTransaction,
    connected,
  } = useWallet();
  const { connection } = useConnection();
  const [vestingAccounts, setVestingAccounts] = useState<
    { pda: PublicKey; accounts: VestingAccount }[]
  >([]);
  const [balances, setBalances] = useState<Record<string, number>>({});
  const [loadingIndex, setLoadingIndex] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const pageSize = 3;

  useEffect(() => {
    const fetchAccounts = async () => {
      if (!publicKey || !connected) return;

      const provider = new anchor.AnchorProvider(
        connection,
        {
          publicKey,
          signTransaction: signTransaction!,
          signAllTransactions: signAllTransactions!,
        },
        { commitment: "confirmed" }
      );
      const program = new anchor.Program(Idl as any, PROGRAM_ID, provider);

      const rawAccounts = await program.account.vestingAccount.all();
      const sorted = rawAccounts.sort((a, b) => {
        return (
          (b.account as unknown as VestingAccount).startVestingTs.toNumber() -
          (a.account as unknown as VestingAccount).startVestingTs.toNumber()
        );
      });
      const filtered = sorted.filter(
        ({ publicKey }) => !HIDDEN_VESTING_LIST.includes(publicKey.toBase58())
      );
      const allVesting = filtered.map(({ publicKey, account }) => ({
        pda: publicKey,
        accounts: account as unknown as VestingAccount,
      }));

      setVestingAccounts(allVesting);
    };

    fetchAccounts();
  }, [publicKey, connected, connection, signTransaction, signAllTransactions]);

  useEffect(() => {
    const fetchBalances = async () => {
      const currentPageAccounts = vestingAccounts.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
      );

      const newBalances: Record<string, number> = {};
      for (const acc of currentPageAccounts) {
        const vestedAmount = acc.accounts.vestedAmount.toNumber() / 10 ** 9;
        try {
          const ata = await getAssociatedTokenAddress(
            new PublicKey(TOKEN_MINT),
            acc.pda,
            true
          );
          const tokenAccount = await getAccount(connection, ata);
          newBalances[acc.pda.toBase58()] =
            Number(tokenAccount.amount) / 10 ** 9 + vestedAmount;
        } catch (err) {
          newBalances[acc.pda.toBase58()] = vestedAmount;
        }
      }

      setBalances((prev) => ({ ...prev, ...newBalances }));
    };

    if (vestingAccounts.length > 0) fetchBalances();
  }, [vestingAccounts, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const filteredAccounts = vestingAccounts.filter((acc) =>
    acc.accounts.beneficiary
      .toBase58()
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredAccounts.length / pageSize);

  const paginatedVestingAccounts = filteredAccounts.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const onDeposit = async (
    vestingPda: PublicKey,
    vestingAccount: VestingAccount,
    index: number
  ) => {
    try {
      if (!publicKey || !connection) throw new Error("Wallet not connected");

      setLoadingIndex(index);

      const userTokenAccount = await getAssociatedTokenAddress(
        new PublicKey(TOKEN_MINT),
        publicKey
      );

      const vestingTokenAccount = await getAssociatedTokenAddress(
        new PublicKey(TOKEN_MINT),
        vestingPda,
        true
      );

      const amount = vestingAccount.totalAmount.sub(
        vestingAccount.vestedAmount
      );
      const ix = createTransferInstruction(
        userTokenAccount,
        vestingTokenAccount,
        publicKey,
        amount,
        []
      );

      const tx = new anchor.web3.Transaction().add(ix);
      const signature = await sendTransaction(tx, connection);
      console.log("Deposit tx:", signature);
    } catch (err) {
      console.error("Failed to deposit:", err);
    } finally {
      setLoadingIndex(null);
    }
  };

  const onWithdraw = async (
    vestingPda: PublicKey,
    vestingAccount: VestingAccount,
    index: number
  ) => {
    if (!publicKey || !connection) throw new Error("Wallet not connected");

    setLoadingIndex(index);

    try {
      const provider = new anchor.AnchorProvider(
        connection,
        {
          publicKey,
          signTransaction: signTransaction!,
          signAllTransactions: signAllTransactions!,
        },
        { commitment: "confirmed" }
      );

      const program = new anchor.Program(Idl as any, PROGRAM_ID, provider);

      const vestingTokenAccount = await getAssociatedTokenAddress(
        vestingAccount.mint,
        vestingPda,
        true
      );

      const adminTokenAccount = await getAssociatedTokenAddress(
        vestingAccount.mint,
        publicKey,
        true
      );

      const [userStatePda] = anchor.web3.PublicKey.findProgramAddressSync(
        [Buffer.from("user_state"), vestingAccount.beneficiary.toBuffer()],
        program.programId
      );

      const [configPda] = anchor.web3.PublicKey.findProgramAddressSync(
        [Buffer.from("config")],
        program.programId
      );

      const signature = await program.methods
        .emergencyWithdraw(new anchor.BN(index))
        .accounts({
          vesting: vestingPda,
          vestingTokenAccount,
          adminTokenAccount,
          config: configPda,
          userState: userStatePda,
          admin: publicKey,
        })
        .rpc();
      console.log("✅ Withdraw successfully:", signature);
    } catch (err) {
      console.error("Failed to withdraw:", err);
    } finally {
      setLoadingIndex(null);
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(text);
    setTimeout(() => setCopiedKey(null), 1500);
  };

  if (!vestingAccounts.length) return null;

  return (
    <div className="bit-card w-full">
      <div className="flex justify-between items-center mb-4">
        <h2 className="bit-title">Vesting Created</h2>
        <input
          type="text"
          placeholder="Search beneficiary..."
          className="bit-input px-3 py-2 border border-gray-300 rounded-md text-sm"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value.trim())}
        />
      </div>

      {paginatedVestingAccounts.map((acc, index) => (
        <div
          key={index}
          style={{
            border: "1px solid #ccc",
            borderRadius: "8px",
            padding: "16px",
            boxSizing: "border-box",
          }}
          className="mt-2"
        >
          <p className="bit-text flex items-center space-x-2">
            <span>Vesting PDA: {shortenAddress(acc.pda.toBase58())}</span>
            <button
              className="bg-black p-1 rounded text-white hover:bg-gray-800"
              onClick={() => handleCopy(acc.pda.toBase58())}
            >
              {copiedKey === acc.pda.toBase58() ? <FiCheck /> : <FiClipboard />}
            </button>
          </p>
          <p className="bit-text flex items-center space-x-2">
            <span>
              Beneficiary: {shortenAddress(acc.accounts.beneficiary.toBase58())}
            </span>
            <button
              className="bg-black p-1 rounded text-white hover:bg-gray-800"
              onClick={() => handleCopy(acc.accounts.beneficiary.toBase58())}
            >
              {copiedKey === acc.accounts.beneficiary.toBase58() ? (
                <FiCheck />
              ) : (
                <FiClipboard />
              )}
            </button>
          </p>
          <p className="bit-text">
            TGE:{" "}
            {new Date(
              acc.accounts.tgeTs.toNumber() * 1000
            ).toLocaleDateString()}
          </p>
          <p className="bit-text">
            Total Amount:{" "}
            {formatAmount(acc.accounts.totalAmount.toNumber() / 10 ** 9)} AVA
          </p>
          <p className="bit-text">
            TGE Amount:{" "}
            {formatAmount(acc.accounts.tgeAmount.toNumber() / 10 ** 9)} AVA
          </p>
          <p className="bit-text">
            Start:{" "}
            {new Date(acc.accounts.startVestingTs * 1000).toLocaleDateString()}
          </p>
          <p className="bit-text">
            End:{" "}
            {new Date(acc.accounts.endVestingTs * 1000).toLocaleDateString()}
          </p>
          <p className="bit-text">
            Deposit Balance:{" "}
            {balances[acc.pda.toBase58()] !== undefined
              ? `${formatAmount(balances[acc.pda.toBase58()])} AVA (${(balances[acc.pda.toBase58()] / (acc.accounts.totalAmount.toNumber() / 10 ** 9)) * 100}%)`
              : "Loading..."}
          </p>
          <div className="flex justify-between mt-2">
            <div>
              <button
                className="bit-button"
                onClick={() => onWithdraw(acc.pda, acc.accounts, index)}
                disabled={loadingIndex === index}
              >
                {loadingIndex === index ? "Processing..." : "Withdraw"}
              </button>
            </div>
            <div>
              <button
                className="bit-button"
                onClick={() => onDeposit(acc.pda, acc.accounts, index)}
                disabled={loadingIndex === index}
              >
                {loadingIndex === index ? "Processing..." : "Deposit"}
              </button>
            </div>
          </div>
        </div>
      ))}

      {/* Pagination Controls */}
      <div className="flex justify-center mt-4 space-x-2">
        <button
          className="bit-button"
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <span className="text-sm text-gray-600 self-center">
          Page {currentPage} of {totalPages}
        </span>
        <button
          className="bit-button"
          onClick={() =>
            setCurrentPage((prev) => (prev < totalPages ? prev + 1 : prev))
          }
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default VestingCreatedList;
